export interface MortgageInput {
  propertyPrice: number
  downPaymentPercent: number
  loanYears: number
  annualRate: number
}

export type InstallmentType = 'equal' | 'decreasing'

export interface MortgageResult {
  loanAmount: number
  downPayment: number
  monthlyPayment: number       // For equal: the fixed payment. For decreasing: first (highest) payment.
  lastMonthlyPayment: number   // For equal: same as monthlyPayment. For decreasing: last (lowest) payment.
  totalCost: number
  totalInterest: number
  annualRate: number
  installmentType: InstallmentType
}

export interface AmortizationRow {
  month: number
  year: number
  payment: number
  principal: number
  interest: number
  remainingBalance: number
  totalPrincipalPaid: number
  totalInterestPaid: number
}

export type AffordabilityLevel = 'comfortable' | 'moderate' | 'tight' | 'risky'

// --- Core Calculations ---

function getLoanAmount(input: MortgageInput): number {
  const downPayment = input.propertyPrice * (input.downPaymentPercent / 100)
  return input.propertyPrice - downPayment
}

export function calculateMortgage(
  input: MortgageInput,
  type: InstallmentType = 'equal'
): MortgageResult {
  const downPayment = input.propertyPrice * (input.downPaymentPercent / 100)
  const loanAmount = input.propertyPrice - downPayment
  const monthlyRate = input.annualRate / 100 / 12
  const totalMonths = input.loanYears * 12

  if (type === 'decreasing') {
    const fixedPrincipal = loanAmount / totalMonths
    let totalCost = 0
    let firstPayment = 0
    let lastPayment = 0

    for (let month = 1; month <= totalMonths; month++) {
      const remainingBalance = loanAmount - fixedPrincipal * (month - 1)
      const interest = remainingBalance * monthlyRate
      const payment = fixedPrincipal + interest
      totalCost += payment
      if (month === 1) firstPayment = payment
      if (month === totalMonths) lastPayment = payment
    }

    return {
      loanAmount,
      downPayment,
      monthlyPayment: firstPayment,
      lastMonthlyPayment: lastPayment,
      totalCost,
      totalInterest: totalCost - loanAmount,
      annualRate: input.annualRate,
      installmentType: type,
    }
  }

  // Equal (annuity)
  let monthlyPayment: number
  if (monthlyRate === 0) {
    monthlyPayment = loanAmount / totalMonths
  } else {
    monthlyPayment =
      (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
      (Math.pow(1 + monthlyRate, totalMonths) - 1)
  }

  const totalCost = monthlyPayment * totalMonths
  const totalInterest = totalCost - loanAmount

  return {
    loanAmount,
    downPayment,
    monthlyPayment,
    lastMonthlyPayment: monthlyPayment,
    totalCost,
    totalInterest,
    annualRate: input.annualRate,
    installmentType: type,
  }
}

// --- Amortization Schedules ---

export function generateAmortizationSchedule(
  input: MortgageInput,
  type: InstallmentType = 'equal'
): AmortizationRow[] {
  const loanAmount = getLoanAmount(input)
  const monthlyRate = input.annualRate / 100 / 12
  const totalMonths = input.loanYears * 12
  const schedule: AmortizationRow[] = []

  let remainingBalance = loanAmount
  let totalPrincipalPaid = 0
  let totalInterestPaid = 0

  if (type === 'decreasing') {
    const fixedPrincipal = loanAmount / totalMonths

    for (let month = 1; month <= totalMonths; month++) {
      const interest = remainingBalance * monthlyRate
      const principal = fixedPrincipal
      const payment = principal + interest
      remainingBalance = Math.max(0, remainingBalance - principal)
      totalPrincipalPaid += principal
      totalInterestPaid += interest

      schedule.push({
        month,
        year: Math.ceil(month / 12),
        payment,
        principal,
        interest,
        remainingBalance,
        totalPrincipalPaid,
        totalInterestPaid,
      })
    }
  } else {
    const monthlyPayment =
      monthlyRate === 0
        ? loanAmount / totalMonths
        : (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
          (Math.pow(1 + monthlyRate, totalMonths) - 1)

    for (let month = 1; month <= totalMonths; month++) {
      const interest = remainingBalance * monthlyRate
      const principal = monthlyPayment - interest
      remainingBalance = Math.max(0, remainingBalance - principal)
      totalPrincipalPaid += principal
      totalInterestPaid += interest

      schedule.push({
        month,
        year: Math.ceil(month / 12),
        payment: monthlyPayment,
        principal,
        interest,
        remainingBalance,
        totalPrincipalPaid,
        totalInterestPaid,
      })
    }
  }

  return schedule
}

export function generateYearlySummary(
  input: MortgageInput,
  type: InstallmentType = 'equal'
): { year: number; principal: number; interest: number; remainingBalance: number }[] {
  const schedule = generateAmortizationSchedule(input, type)
  return scheduleToYearlySummary(schedule)
}

// --- Affordability ---

export function assessAffordability(
  monthlyPayment: number,
  monthlyIncome: number
): AffordabilityLevel {
  const ratio = monthlyPayment / monthlyIncome
  if (ratio <= 0.25) return 'comfortable'
  if (ratio <= 0.35) return 'moderate'
  if (ratio <= 0.5) return 'tight'
  return 'risky'
}

export function getAffordabilityPercent(monthlyPayment: number, monthlyIncome: number): number {
  return Math.min(100, (monthlyPayment / monthlyIncome) * 100)
}

// --- Overpayment Simulator ---

export interface OverpaymentComparison {
  baseTotalMonths: number
  baseTotalInterest: number
  overpaymentTotalMonths: number
  overpaymentTotalInterest: number
  monthsSaved: number
  interestSaved: number
  baseYearlySummary: YearlySummaryRow[]
  overpaymentYearlySummary: YearlySummaryRow[]
}

export interface YearlySummaryRow {
  year: number
  principal: number
  interest: number
  remainingBalance: number
}

function scheduleToYearlySummary(schedule: AmortizationRow[]): YearlySummaryRow[] {
  const years: Map<number, YearlySummaryRow> = new Map()
  for (const row of schedule) {
    const existing = years.get(row.year)
    if (existing) {
      existing.principal += row.principal
      existing.interest += row.interest
      existing.remainingBalance = row.remainingBalance
    } else {
      years.set(row.year, {
        year: row.year,
        principal: row.principal,
        interest: row.interest,
        remainingBalance: row.remainingBalance,
      })
    }
  }
  return Array.from(years.values())
}

function generateOverpaymentSchedule(
  input: MortgageInput,
  monthlyOverpayment: number,
  type: InstallmentType = 'equal'
): AmortizationRow[] {
  const loanAmount = getLoanAmount(input)
  const monthlyRate = input.annualRate / 100 / 12
  const totalMonths = input.loanYears * 12
  const schedule: AmortizationRow[] = []

  let remainingBalance = loanAmount
  let totalPrincipalPaid = 0
  let totalInterestPaid = 0
  let month = 0

  if (type === 'decreasing') {
    const fixedPrincipal = loanAmount / totalMonths

    while (remainingBalance > 0.01) {
      month++
      const interest = remainingBalance * monthlyRate
      const basePrincipal = Math.min(fixedPrincipal, remainingBalance)
      const extraPrincipal = Math.min(monthlyOverpayment, remainingBalance - basePrincipal)
      const totalPrincipal = basePrincipal + extraPrincipal
      const payment = totalPrincipal + interest

      remainingBalance = Math.max(0, remainingBalance - totalPrincipal)
      totalPrincipalPaid += totalPrincipal
      totalInterestPaid += interest

      schedule.push({ month, year: Math.ceil(month / 12), payment, principal: totalPrincipal, interest, remainingBalance, totalPrincipalPaid, totalInterestPaid })
      if (month > 600) break
    }
  } else {
    const { monthlyPayment } = calculateMortgage(input, 'equal')

    while (remainingBalance > 0.01) {
      month++
      const interest = remainingBalance * monthlyRate
      const regularPrincipal = monthlyPayment - interest
      const extraPrincipal = Math.min(monthlyOverpayment, remainingBalance - regularPrincipal)
      const totalPrincipal = regularPrincipal + extraPrincipal
      const payment = monthlyPayment + extraPrincipal

      remainingBalance = Math.max(0, remainingBalance - totalPrincipal)
      totalPrincipalPaid += totalPrincipal
      totalInterestPaid += interest

      schedule.push({ month, year: Math.ceil(month / 12), payment, principal: totalPrincipal, interest, remainingBalance, totalPrincipalPaid, totalInterestPaid })
      if (month > 600) break
    }
  }

  return schedule
}

// --- Rent vs Buy ---

export interface YearlyCumulative {
  year: number
  cumulativeBuy: number
  cumulativeRent: number
}

export interface RentVsBuyResult {
  totalMortgageCost: number
  totalRentCost: number
  propertyValue: number
  netBuyPosition: number
  netRentPosition: number
  advantage: number
  buyWins: boolean
  breakEvenMonth: number | null
  yearlyCumulativeData: YearlyCumulative[]
}

export function calculateRentVsBuy(
  input: MortgageInput,
  mortgageResult: MortgageResult,
  monthlyRent: number,
): RentVsBuyResult {
  const totalMonths = input.loanYears * 12
  const totalMortgageCost = mortgageResult.downPayment + mortgageResult.totalCost
  const totalRentCost = monthlyRent * totalMonths
  const propertyValue = input.propertyPrice
  const netBuyPosition = propertyValue - totalMortgageCost
  const netRentPosition = -totalRentCost

  // Find break-even month: when cumulative rent spent exceeds cumulative mortgage cash spent
  // Pure cash-out comparison (ignoring property value):
  //   Buy accumulates: down payment + monthly payments
  //   Rent accumulates: monthly rent
  // Break-even = the month when cumulativeRent >= cumulativeBuy
  let breakEvenMonth: number | null = null
  let cumulativeBuy = mortgageResult.downPayment
  let cumulativeRent = 0
  const yearlyCumulativeData: YearlyCumulative[] = []

  for (let month = 1; month <= totalMonths; month++) {
    cumulativeBuy += mortgageResult.monthlyPayment
    cumulativeRent += monthlyRent
    if (breakEvenMonth === null && cumulativeRent >= cumulativeBuy) {
      breakEvenMonth = month
    }
    // Snapshot at end of each year
    if (month % 12 === 0) {
      yearlyCumulativeData.push({
        year: month / 12,
        cumulativeBuy,
        cumulativeRent,
      })
    }
  }

  const advantage = netBuyPosition - netRentPosition

  return {
    totalMortgageCost,
    totalRentCost,
    propertyValue,
    netBuyPosition,
    netRentPosition,
    advantage,
    buyWins: advantage > 0,
    breakEvenMonth,
    yearlyCumulativeData,
  }
}

// --- Overpayment Simulator ---

export function generateOverpaymentComparison(
  input: MortgageInput,
  monthlyOverpayment: number,
  type: InstallmentType = 'equal'
): OverpaymentComparison {
  const baseSchedule = generateAmortizationSchedule(input, type)
  const overpaymentSchedule = generateOverpaymentSchedule(input, monthlyOverpayment, type)

  const baseTotalMonths = baseSchedule.length
  const baseTotalInterest = baseSchedule[baseSchedule.length - 1]?.totalInterestPaid ?? 0
  const overpaymentTotalMonths = overpaymentSchedule.length
  const overpaymentTotalInterest =
    overpaymentSchedule[overpaymentSchedule.length - 1]?.totalInterestPaid ?? 0

  return {
    baseTotalMonths,
    baseTotalInterest,
    overpaymentTotalMonths,
    overpaymentTotalInterest,
    monthsSaved: baseTotalMonths - overpaymentTotalMonths,
    interestSaved: baseTotalInterest - overpaymentTotalInterest,
    baseYearlySummary: scheduleToYearlySummary(baseSchedule),
    overpaymentYearlySummary: scheduleToYearlySummary(overpaymentSchedule),
  }
}
